"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Copy, Check, Trash2, ArrowRightLeft } from "lucide-react"
import { toast } from "sonner"

type Mode = "encode" | "decode"

// Named HTML entities map
const namedEntities: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
  " ": "&nbsp;",
  "¢": "&cent;",
  "£": "&pound;",
  "¥": "&yen;",
  "€": "&euro;",
  "©": "&copy;",
  "®": "&reg;",
  "™": "&trade;",
  "—": "&mdash;",
  "–": "&ndash;",
  "…": "&hellip;",
  "•": "&bull;",
  "§": "&sect;",
  "¶": "&para;",
  "†": "&dagger;",
  "‡": "&Dagger;",
  "°": "&deg;",
  "±": "&plusmn;",
  "÷": "&divide;",
  "×": "&times;",
  "½": "&frac12;",
  "¼": "&frac14;",
  "¾": "&frac34;",
  "←": "&larr;",
  "→": "&rarr;",
  "↑": "&uarr;",
  "↓": "&darr;",
  "↔": "&harr;",
  "♠": "&spades;",
  "♣": "&clubs;",
  "♥": "&hearts;",
  "♦": "&diams;",
}

// Reverse map for decoding
const reverseEntities: Record<string, string> = Object.fromEntries(
  Object.entries(namedEntities).map(([char, entity]) => [entity, char])
)

function encodeHTML(text: string, useNamedEntities: boolean, encodeAllChars: boolean): string {
  let result = ""

  for (const char of text) {
    const code = char.charCodeAt(0)

    // Always encode < > & " '
    if (namedEntities[char] && ["&", "<", ">", '"', "'"].includes(char)) {
      result += useNamedEntities ? namedEntities[char] : `&#${code};`
    }
    // Named entity exists and we want to use them
    else if (useNamedEntities && namedEntities[char]) {
      result += namedEntities[char]
    }
    // Encode non-ASCII or all chars
    else if (encodeAllChars && code > 127) {
      result += `&#${code};`
    }
    // Keep ASCII printable as-is (unless encodeAllChars)
    else if (!encodeAllChars && code >= 32 && code <= 126) {
      result += char
    }
    // Encode everything else
    else if (encodeAllChars && code > 32 && code !== 127) {
      result += `&#${code};`
    }
    else {
      result += char
    }
  }

  return result
}

function decodeHTML(text: string): string {
  // First decode named entities
  let result = text
  for (const [entity, char] of Object.entries(reverseEntities)) {
    result = result.split(entity).join(char)
  }

  // Then decode numeric entities (&#123; or &#x7B;)
  result = result.replace(/&#(\d+);/g, (_, code) => {
    return String.fromCharCode(parseInt(code, 10))
  })

  result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, code) => {
    return String.fromCharCode(parseInt(code, 16))
  })

  return result
}

const sampleText = `<div class="hello">Hello & welcome!</div>
Special chars: © ® ™ € £ ¥
Arrows: ← → ↑ ↓
"Quoted" text with 'apostrophes'`

export function HtmlEntityConverter() {
  const [mode, setMode] = useState<Mode>("encode")
  const [input, setInput] = useState("")
  const [output, setOutput] = useState("")
  const [useNamedEntities, setUseNamedEntities] = useState(true)
  const [encodeAllChars, setEncodeAllChars] = useState(false)
  const [copied, setCopied] = useState(false)

  const convert = useCallback(() => {
    if (!input.trim()) {
      setOutput("")
      return
    }

    try {
      if (mode === "encode") {
        const result = encodeHTML(input, useNamedEntities, encodeAllChars)
        setOutput(result)
        toast.success("Encoded successfully")
      } else {
        const result = decodeHTML(input)
        setOutput(result)
        toast.success("Decoded successfully")
      }
    } catch {
      toast.error("Conversion failed")
    }
  }, [input, mode, useNamedEntities, encodeAllChars])

  const toggleMode = () => {
    setMode((prev) => (prev === "encode" ? "decode" : "encode"))
    setInput(output)
    setOutput("")
  }

  const loadSample = () => {
    setInput(sampleText)
    setOutput("")
  }

  const clear = () => {
    setInput("")
    setOutput("")
  }

  const copyOutput = async () => {
    if (!output) return
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    toast.success("Copied to clipboard")
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>HTML Entity {mode === "encode" ? "Encoder" : "Decoder"}</CardTitle>
          <CardDescription>
            {mode === "encode"
              ? "Convert special characters to HTML entities"
              : "Convert HTML entities back to characters"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Options */}
          <div className="flex flex-wrap gap-4 items-center">
            {mode === "encode" && (
              <>
                <div className="flex items-center gap-2">
                  <Switch
                    id="named"
                    checked={useNamedEntities}
                    onCheckedChange={setUseNamedEntities}
                  />
                  <Label htmlFor="named">Use named entities</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="all"
                    checked={encodeAllChars}
                    onCheckedChange={setEncodeAllChars}
                  />
                  <Label htmlFor="all">Encode non-ASCII</Label>
                </div>
              </>
            )}

            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={loadSample}>
                Load Sample
              </Button>
              <Button variant="outline" size="sm" onClick={clear}>
                <Trash2 className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </div>
          </div>

          {/* Input */}
          <div className="space-y-2">
            <Label htmlFor="input">
              {mode === "encode" ? "Plain Text" : "HTML Entities"}
            </Label>
            <Textarea
              id="input"
              placeholder={
                mode === "encode"
                  ? "Enter text with special characters..."
                  : "Enter HTML entities to decode..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="font-mono text-sm min-h-[150px]"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <Button onClick={convert} disabled={!input.trim()}>
              {mode === "encode" ? "Encode" : "Decode"}
            </Button>
            <Button variant="outline" onClick={toggleMode}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Switch to {mode === "encode" ? "Decode" : "Encode"}
            </Button>
          </div>

          {/* Output */}
          {output && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>
                  {mode === "encode" ? "Encoded HTML" : "Decoded Text"}
                </Label>
                <Button variant="ghost" size="sm" onClick={copyOutput}>
                  {copied ? (
                    <Check className="h-4 w-4 mr-1 text-green-500" />
                  ) : (
                    <Copy className="h-4 w-4 mr-1" />
                  )}
                  Copy
                </Button>
              </div>
              <Textarea
                value={output}
                readOnly
                className="font-mono text-sm min-h-[150px] bg-muted"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Common Entities Reference */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Common HTML Entities</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 text-sm">
            {[
              { char: "&", entity: "&amp;", name: "Ampersand" },
              { char: "<", entity: "&lt;", name: "Less than" },
              { char: ">", entity: "&gt;", name: "Greater than" },
              { char: '"', entity: "&quot;", name: "Quote" },
              { char: "'", entity: "&#39;", name: "Apostrophe" },
              { char: " ", entity: "&nbsp;", name: "Non-breaking space" },
              { char: "©", entity: "&copy;", name: "Copyright" },
              { char: "®", entity: "&reg;", name: "Registered" },
              { char: "™", entity: "&trade;", name: "Trademark" },
              { char: "€", entity: "&euro;", name: "Euro" },
              { char: "£", entity: "&pound;", name: "Pound" },
              { char: "—", entity: "&mdash;", name: "Em dash" },
            ].map((item) => (
              <div
                key={item.entity}
                className="flex items-center gap-2 p-2 rounded bg-muted/50"
              >
                <span className="text-lg w-6 text-center">{item.char === " " ? "␣" : item.char}</span>
                <div className="min-w-0">
                  <code className="text-xs text-primary">{item.entity}</code>
                  <p className="text-xs text-muted-foreground truncate">{item.name}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
